import { callable } from '../callable';
import type {
  MembershipStage,
  StageChange,
  NetworkSubmission,
  MembershipSubmissionSummary,
  ReviewerDecision,
  EmittableKind,
  CapNetworkSubmission,
  CapAssistedAnalysisProps,
} from '@dosfilos/domain';

// ─── Cliente de la RED (R1-PR6) ─────────────────────────────────────────────
// Todo lo que el revisor de TMAI hace pasa por acá, y todo apunta al tenant de
// la RED. No hay un solo método que lea el tenant de un seminario: esa es la
// frontera de la publicación por copia, y el cliente la respeta por no tener
// forma de cruzarla, no por disciplina de quien lo use.

function call<Req, Res>(name: string) {
  const fn = callable<Req, Res>(name);
  return async (data: Req): Promise<Res> => (await fn(data)).data;
}

/** Una ronda anterior, tal como la ve el panel: qué fue y qué se le dijo. Sin
 *  paquete — el expediente completo se abre entrando a esa ronda. */
export interface PreviousRound {
  id: string;
  round: number;
  stage: MembershipStage;
  status: string;
  submissionKind: string;
  submittedAt: string;
  decidedAt?: string;
  decision?: string;
  observations?: string;
  /** Lo que dijo la PLATAFORMA, sin idioma congelado. */
  systemNote?: { code: string; params?: Record<string, string | number> };
}

export const networkApi = {
  /** La bandeja del revisor. `tenantId` es el tenant de la RED.
   *
   *  Trae los umbrales de aviso en la misma respuesta: son del PROPIO tenant del
   *  revisor, así que no hay lectura cruzada, y evitan una segunda llamada. Si el
   *  tenant no los declaró, `noticeDays` viene ausente y la UI no avisa. */
  listSubmissions: call<
    { tenantId: string; status?: string },
    { submissions: NetworkSubmission[]; noticeDays?: { formal?: number; advisory?: number } }
  >('networkListSubmissions'),

  /** Rondas de propuesta CAP (Fase 2). Llamada APARTE y no un parámetro de la
   *  anterior: son colecciones distintas con formas distintas, y un solo
   *  endpoint que devolviera una u otra según un flag obligaría a quien lo
   *  consume a adivinar qué recibió. */
  listCapSubmissions: call<
    { tenantId: string; status?: string },
    { submissions: CapNetworkSubmission[] }
  >('networkListCapSubmissions'),

  /** El detalle, MÁS la etapa alcanzada y la historia del seminario — ambas leídas
   *  del tenant de la RED (`networkAffiliations`), nunca del seminario. De ahí sale
   *  el plazo de candidatura, que no se puede derivar de la ronda sola. */
  getSubmissionDetail: call<
    { tenantId: string; submissionId: string },
    {
      submission: NetworkSubmission;
      /** Rondas anteriores del MISMO seminario, sin paquete. */
      previous: PreviousRound[];
      achievedStage?: MembershipStage;
      stageHistory?: StageChange[];
    }
  >('networkGetSubmissionDetail'),

  /** Aprobar / devolver / rechazar. Aprobar solo está disponible para una
   *  SOLICITUD: el servidor rechaza aprobar una consulta. */
  decideSubmission: call<
    {
      tenantId: string;
      submissionId: string;
      decision: ReviewerDecision;
      observations?: string;
    },
    { status: ReviewerDecision }
  >('networkDecideSubmission'),

  // ── CAP Fase 2 ────────────────────────────────────────────────────────────
  getCapSubmissionDetail: call<{ tenantId: string; submissionId: string }, CapNetworkSubmission>(
    'networkGetCapSubmissionDetail',
  ),

  /** Pide un BORRADOR de análisis sobre la ronda. No decide nada: devuelve la
   *  sugerencia para que el revisor la lea. Si el modelo falla, la respuesta
   *  llega con `status: 'failed'` en vez de un error — la pantalla del revisor
   *  funciona entera sin esto. */
  generateCapAnalysis: call<
    {
      tenantId: string;
      submissionId: string;
      locale?: 'es' | 'en';
      /** Los nombres de las áreas temáticas en el idioma de la pantalla. Viajan
       *  desde acá y no viven en `functions` porque el texto que lee una persona
       *  se traduce en i18n (CLAUDE.md), y duplicar la tabla del otro lado la
       *  dejaría derivando. Sin esto el modelo escribe `THEOLOGY` en un borrador
       *  que va a leer el centro. */
      areaLabels?: Record<string, string>;
      /** El enunciado de cada hallazgo tal como lo lee el revisor en la portada.
       *  El código por sí solo no dice qué se midió, y el modelo lo deduce mal. */
      findingStatements?: string[];
    },
    CapAssistedAnalysisProps
  >('networkGenerateCapAnalysis'),

  /** Los análisis guardados de una ronda, del más nuevo al más viejo. */
  listCapAnalyses: call<
    { tenantId: string; submissionId: string },
    { analyses: CapAssistedAnalysisProps[] }
  >('networkListCapAnalyses'),

  /** APROBAR OTORGA LA CERTIFICACIÓN del programa, en la misma transacción. Por
   *  eso la respuesta trae `certificationId`: no es un dato de adorno, es la
   *  prueba de que el acto tuvo la consecuencia que dice tener. */
  decideCapSubmission: call<
    {
      tenantId: string;
      submissionId: string;
      decision: ReviewerDecision;
      observations?: string;
      /** Sólo con `approved_with_conditions`. El servidor valida que estén ahí
       *  y sólo ahí: la pantalla no es la que hace cumplir esa regla. */
      conditions?: {
        items: { code: string; area?: string; note?: string }[];
        reviewBy: string;
      };
    },
    { status: ReviewerDecision; certificationId?: string }
  >('networkDecideCapSubmission'),

  /** Retira una certificación ya otorgada. El motivo es obligatorio y viaja al
   *  espejo del centro: es lo ÚNICO que la institución recibe para entender qué
   *  pasó, porque el espejo no lleva quién la revocó. */
  revokeCapCertification: call<
    { tenantId: string; certificationId: string; reason: string },
    { certificationId: string; seminaryTenantId: string }
  >('networkRevokeCapCertification'),
};

export const seminaryNetworkApi = {
  /** El seminario presenta. `submissionKind` NO tiene default: elegir entre pedir
   *  feedback y presentar la solicitud es la decisión más cara del proceso — la
   *  solicitud de candidatura arranca un plazo de dos años. */
  submit: call<
    { tenantId: string; cycleYear?: number; submissionKind: EmittableKind },
    MembershipSubmissionSummary
  >('membershipSubmitToNetwork'),

  listOwnSubmissions: call<{ tenantId: string }, MembershipSubmissionSummary[]>(
    'membershipListSubmissions',
  ),

  withdraw: call<{ tenantId: string; submissionId: string }, { status: string }>(
    'membershipWithdrawSubmission',
  ),
};
